import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { LoadWeightCells, TripIntegrationDetails, EventActor } from 'components/Erp/IntegrationTrip';
import React, { useState, useCallback } from 'react';
import styles from '../ReportCard.module.scss';
import { DateTime, Vehicle, Duration } from 'components/Erp/Display';
import { MapPin, Map, X, CircleCheck, SearchX, ChevronRight, ChevronDown } from 'lucide-react';
import { EmptyState } from 'components/Erp/Presentation';
import Pagination from './Pagination';
import SearchBar from './SearchBar';
import DriverIdentity from './DriverIdentity';
import { GeoField, InfoItem, PhoneLink } from './LoadDetailFields';
import MiniMap from '../MiniMap';
import { getDriverDocuments } from 'service/DriverService';
import { canReadDriverDocuments, canReadLoadPhotos } from '../utils/access';
import { isSingleLoadClick, shouldToggleLoadRow } from '../utils/rowInteraction';

const CompletedLoadsTableView = ({
  completedLoads,
  completedCount,
  completedNext,
  completedPrev,
  completedPage,
  setCompletedPage,
  completedSearch,
  setCompletedSearch,
  user,
  loading = false,
  pending = false,
  error = null,
}) => {
  useInterfaceLanguage();
  const [visibleMaps, setVisibleMaps] = useState({});
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailMaps, setDetailMaps] = useState({});
  // driverDocs: { [loadId]: { loading, error, docs } }
  const [driverDocs, setDriverDocs] = useState({});
  const [docPreview, setDocPreview] = useState(null); // { url, label }

  const toggleMap = (id, geoType) => {
    const key = `${id}-${geoType}`;
    setVisibleMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDetailMap = (key) => {
    setDetailMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchDocumentsIfNeeded = useCallback(async (load) => {
    const canSee = canReadDriverDocuments(user);
    if (!canSee) return;
    const driverId = load.driver_id || load.driver;
    if (!driverId) return;
    const loadId = load.id;
    if (driverDocs[loadId]) return; // already fetched
    setDriverDocs(prev => ({ ...prev, [loadId]: { loading: true, error: null, docs: [] } }));
    try {
      const res = await getDriverDocuments(driverId);
      setDriverDocs(prev => ({ ...prev, [loadId]: { loading: false, error: null, docs: res.data } }));
    } catch (e) {
      setDriverDocs(prev => ({ ...prev, [loadId]: { loading: false, error: tr("Ошибка загрузки"), docs: [] } }));
    }
  }, [user, driverDocs]);

  const handleRowClick = (load) => {
    const id = load.id;
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
      setDetailMaps({});
      fetchDocumentsIfNeeded(load);
    }
  };
  const renderDetailCard = (load) => {
    const baseName = load.top_base_name || load.chpp_name || load.private_point_name || '-';
    const basePhone = load.top_base_data?.phone || '-';
    const canSeeDocs = canReadDriverDocuments(user);
    const docsState = driverDocs[load.id];

    const baseType = load.top_base_name
      ? tr("Топливная база")
      : load.chpp_name
      ? tr("ТЭЦ")
      : load.private_point_name
      ? tr("Соц. объект / МСУ")
      : null;

    return (
      <div className={styles.detailCard}>

        {/* ── Шапка: заголовок + бейджи времени ── */}
        <div className={styles.detailCardTopBar}>
          <div className={styles.detailCardTitle}>
            <span></span>{tr(" Подробности погрузки")}</div>
          <div className={styles.detailDurationBadges}>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeLoading}`}>
              <Duration value={load.duration_str_loading} label={tr("Погрузка")} />
            </div>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeDelivery}`}>
              <Duration value={load.duration_str} label={tr("Доставка")} />
            </div>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeTotal}`}>
              <Duration value={load.total_duration_str} label={tr("Общее")} />
            </div>
          </div>
        </div>

        <div className={styles.detailBody}><TripIntegrationDetails load={load} />

          {tr(load.notes && (
            <div className={styles.detailNotes}>
              <strong>{tr("Примечания:")}</strong> {tr(load.notes)}
            </div>
          ))}

          {/* ══ Секция 1: Водитель ══ */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>{tr(" Информация о водителе")}</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label={tr("ФИО")} value={load.employee_name || '-'} />
              <InfoItem label={tr("Телефон")} value={<PhoneLink phone={load.employee_phone} />} />
              <InfoItem label={tr("Статус")} value={load.driver_status || load.status || '-'} />
              <InfoItem label={tr("Автомобиль")} value={<Vehicle number={load.vehicle_number} model={load.vehicle_model} />} />
            </div>

            {/* Документы водителя — только для admin/report */}
            {tr(canSeeDocs && (() => {
              const DOC_LABELS = [
                "Фото ТС",
                "Паспорт (л)",
                "Паспорт (о)",
                "Права (л)",
                "Права (о)",
                "Тех.пасп. (л)",
                "Тех.пасп. (о)",
              ];
              const byDesc = {};
              if (docsState?.docs) {
                docsState.docs.forEach(d => {
                  if (d.description) byDesc[d.description] = d;
                });
              }
              // docs not matching any label
              const otherDocs = docsState?.docs?.filter(d => !DOC_LABELS.includes(d.description)) || [];
              return (
                <div className={styles.detailDocsWrap}>
                  <div className={styles.detailDocsTitle}>{tr(" Документы водителя")}</div>
                  {!docsState || docsState.loading ? (
                    <div className={styles.detailDocsLoading}>{tr("Загрузка документов…")}</div>
                  ) : docsState.error ? (
                    <div className={styles.detailDocsError}>{tr(docsState.error)}</div>
                  ) : (
                    <>
                      <div className={styles.detailDocsGrid}>
                        {DOC_LABELS.map(label => {
                          const doc = byDesc[label];
                          const fileUrl = doc ? (doc.file_url || doc.file) : null;
                          const isActive = docPreview?.url === fileUrl && fileUrl;
                          return (
                            <button
                              key={label}
                              className={`${styles.detailDocBtn} ${doc ? styles.detailDocBtnHas : styles.detailDocBtnMissing} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => {
                                if (!fileUrl) return;
                                setDocPreview(isActive ? null : { url: fileUrl, label });
                              }}
                              title={doc ? tr("Открыть: {{value0}}", { value0: label }) : tr("{{value0}} — не загружено", { value0: label })}
                            >
                              <span className={styles.detailDocBtnIcon}>{doc ? '' : '—'}</span>
                              <span className={styles.detailDocBtnLabel}>{tr(label)}</span>
                              {/* {doc && <span className={styles.detailDocBtnArrow}>{isActive ? '▲' : '▼'}</span>} */}
                            </button>
                          );
                        })}
                        {otherDocs.map(doc => {
                          const fileUrl = doc.file_url || doc.file;
                          const isActive = docPreview?.url === fileUrl;
                          return (
                            <button
                              key={doc.id}
                              className={`${styles.detailDocBtn} ${styles.detailDocBtnHas} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => setDocPreview(isActive ? null : { url: fileUrl, label: doc.file_name || tr("Файл") })}
                            >
                              <span className={styles.detailDocBtnIcon}></span>
                              <span className={styles.detailDocBtnLabel}>{tr(doc.description || doc.file_name || tr("Файл"))}</span>
                              {/* <span className={styles.detailDocBtnArrow}>{isActive ? '▲' : '▼'}</span> */}
                            </button>
                          );
                        })}
                        {tr(!docsState?.docs?.length && (
                          <div className={styles.detailDocsEmpty} style={{ gridColumn: '1 / -1' }}>{tr("Документы не загружены")}</div>
                        ))}
                      </div>
                      {tr(docPreview && (
                        <div className={styles.detailDocPreview}>
                          <div className={styles.detailDocPreviewHeader}>
                            <div className={styles.detailDocPreviewTitle}>
                              <span className={styles.detailDocPreviewIcon}></span>
                              <span>{tr(docPreview.label)}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>{tr("↗ Открыть")}</a>
                              <button type="button" aria-label={tr("Закрыть документ")} className={styles.detailDocPreviewClose} onClick={() => setDocPreview(null)}><X size={16} aria-hidden="true" /></button>
                            </div>
                          </div>
                          <div className={styles.detailDocPreviewBody}>
                            <img src={docPreview.url} alt={tr(docPreview.label)} className={styles.detailDocPreviewImg}
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                            />
                            <div style={{ display: 'none', padding: '24px', textAlign: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>{tr("⇩ Скачать файл")}</a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })())}
          </div>

          {/* ══ Секция 2: База назначения ══ */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>{tr(" Информация о базе назначения")}</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label={tr("Название")} value={<span style={{ color: "var(--theme-info-text, #0d6efd)", fontWeight: 700 }}>{tr(baseName)}</span>} />
              <InfoItem label={tr("Телефон")} value={<PhoneLink phone={basePhone} />} />
              {tr(baseType && <InfoItem label={tr("Тип")} value={baseType} />)}
            </div>
            {tr(Array.isArray(load.geo_base) && load.geo_base.length > 0 && (
              <div className={styles.detailSubSection}>
                <div className={styles.detailSubSectionTitle}>{tr(" Точки базы")}</div>
                {load.geo_base.map((g, i) => (
                  <div key={g.id ?? i} className={styles.detailBranchRow}>
                    <span className={styles.detailBranchName}>{tr(g.name)}</span>
                    {tr(g.address && <span className={styles.detailBranchAddr}>{tr(g.address)}</span>)}
                    {tr(g.geo && (
                      <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${g.geo}`, '_blank')}>{tr(" Открыть")}</button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* ══ Секция 3: О погрузке ══ */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>{tr(" О погрузке")}</div>

            {/* Въезд */}
            <div className={styles.detailSubSection}>
              <div className={styles.detailSubSectionTitle}>{tr(" Въезд в Кара-Кече")}</div>
              <div className={styles.detailInfoGrid}>
                <InfoItem label={tr("Время въезда")} value={<DateTime value={load.time_a} />} />
                <InfoItem label={tr("Время отметки")} value={<DateTime value={load.note_a_datetime} />} />
                <InfoItem label={tr("СБ въезда")} value={<EventActor source={load.entered_source} label={load.entered_actor_label} fallback={load.guard_name} entry />} />
                <InfoItem label={tr("Телефон СБ")} value={<PhoneLink phone={load.guard_phone} />} />
              </div>
              {tr(load.geo_a && (
                <div className={styles.detailGeoSection}>
                  <span className={styles.detailGeoLabel}>{tr("Геолокация въезда")}</span>
                  <GeoField geo={load.geo_a} mapKey={`detail-${load.id}-geo_a`} maps={detailMaps} onToggle={toggleDetailMap} />
                </div>
              ))}
              {tr(load.photo_a && (
                <div className={styles.detailPhotoConfirm}>
                  <div className={styles.detailPhotoConfirmLabel}>{tr(" ПОДТВЕРЖДЕНИЕ ВЪЕЗДА")}</div>
                  <a href={load.photo_a} target="_blank" rel="noopener noreferrer">
                    <img src={load.photo_a} alt={tr("Подтверждение въезда")} className={styles.detailPhoto} />
                  </a>
                </div>
              ))}
            </div>

            {/* Выезд */}
            <div className={styles.detailSubSection}>
              <div className={styles.detailSubSectionTitle}>{tr(" Выезд с Кара-Кече")}</div>
              <div className={styles.detailInfoGrid}>
                <InfoItem label={tr("Время выезда")} value={<DateTime value={load.time_a_exit} />} />
                <InfoItem label={tr("Время отметки")} value={<DateTime value={load.note_a_exit_datetime} />} />
                <InfoItem label={tr("СБ выезда")} value={<EventActor source={load.exit_source} label={load.exit_actor_label} fallback={load.exit_guard_name} />} />
                <InfoItem label={tr("Телефон СБ")} value={<PhoneLink phone={load.exit_guard_phone} />} />
              </div>
              {tr(load.geo_a_exit && (
                <div className={styles.detailGeoSection}>
                  <span className={styles.detailGeoLabel}>{tr("Геолокация выезда")}</span>
                  <GeoField geo={load.geo_a_exit} mapKey={`detail-${load.id}-geo_a_exit`} maps={detailMaps} onToggle={toggleDetailMap} />
                </div>
              ))}
            </div>

            {/* Прибытие на базу */}
            <div className={styles.detailSubSection}>
              <div className={styles.detailSubSectionTitle}>{tr(" Прибытие на базу")}</div>
              <div className={styles.detailInfoGrid}>
                <InfoItem label={tr("Время прибытия")} value={<DateTime value={load.time_b} />} />
                <InfoItem label={tr("Время отметки")} value={<DateTime value={load.note_b_datetime} />} />
                <InfoItem label={tr("Оператор базы")} value={load.by_guard_name || '-'} />
                <InfoItem label={tr("Телефон оператора")} value={<PhoneLink phone={load.by_guard_phone} />} />
              </div>
              {tr(load.geo_b && (
                <div className={styles.detailGeoSection}>
                  <span className={styles.detailGeoLabel}>{tr("Геолокация базы")}</span>
                  <GeoField geo={load.geo_b} mapKey={`detail-${load.id}-geo_b`} maps={detailMaps} onToggle={toggleDetailMap} />
                </div>
              ))}
              {tr(load.photo_b && (
                <div className={styles.detailPhotoConfirm}>
                  <div className={styles.detailPhotoConfirmLabel}>{tr(" ПОДТВЕРЖДЕНИЕ ПОГРУЗКИ")}</div>
                  <a href={load.photo_b} target="_blank" rel="noopener noreferrer">
                    <img src={load.photo_b} alt={tr("Подтверждение погрузки")} className={styles.detailPhoto} />
                  </a>
                </div>
              ))}
            </div>

          </div>

        </div>
      </div>
    );
  };

  return (
    <div className={styles.table_info_wrapper}>
      <Pagination
        page={completedPage}
        setPage={setCompletedPage}
        count={completedCount}
        hasNext={!!completedNext}
        hasPrev={!!completedPrev}
        shownCount={completedLoads.length}
        loading={loading || pending}
      />

      <div className={styles.completedSearchRow}>
        <SearchBar
          value={completedSearch}
          onChange={setCompletedSearch}
          onClear={() => setCompletedSearch('')}
          placeholder={tr("Поиск по водителю, машине, базе...")}
        />
        <span className={styles.completedBadge}>{tr(" Завершённые поездки")}</span>
      </div>

      <div className={styles.tableScrollWrap}>
        <table className={`${styles.dataTable} ${styles.completedTable}`} aria-busy={loading || pending}>
          <thead>
            <tr>
              <th>#</th>
              <th>{tr("Машина")}</th>
              <th>{tr("Водитель")}</th>
              <th>{tr("Очередь")}</th><th>{tr("Масса тары")}</th><th>{tr("Брутто · нетто")}</th>
              <th>{tr("База назначения")}</th>
              <th>{tr("Въезд (Кара-Кече)")}</th>
              <th>{tr("Выезд (Кара-Кече)")}</th>
              <th>{tr("Прибытие (База)")}</th>
              <th>{tr("Погрузка")}</th>
              <th>{tr("Доставка")}</th>
              <th>{tr("Общее")}</th>
              <th>{tr("Примечания")}</th>
            </tr>
          </thead>
          <tbody>
            {completedLoads && completedLoads.length > 0 ? (
              completedLoads.map((load, idx) => {
                const driverName = load.employee_name || '-';
                const baseName = load.top_base_name || load.chpp_name || load.private_point_name || '-';
                const rowNumber = (completedPage - 1) * 10 + idx + 1;

                const mapKeyA = `${load.id}-geo_a`;
                const mapKeyAExit = `${load.id}-geo_a_exit`;
                const mapKeyB = `${load.id}-geo_b`;

                return (
                  <React.Fragment key={load.id}>
                    <tr
                      onClick={event => { if (shouldToggleLoadRow(event)) handleRowClick(load); }}
                      style={{ cursor: 'pointer', background: expandedRowId === load.id ? "var(--theme-info-soft, #eef4ff)" : undefined }}
                      title={tr("Нажмите, чтобы открыть или скрыть подробности")}
                    >
                      <td data-label={tr("№")}><button type="button" className={styles.rowDetailButton} aria-label={tr("Подробнее о перевозке {{value0}}", { value0: load.vehicle_number || load.id })} aria-expanded={expandedRowId === load.id} aria-controls={`load-details-completed-${load.id}`} onClick={event => { event.stopPropagation(); if (isSingleLoadClick(event)) handleRowClick(load); }}>{expandedRowId === load.id ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}{tr(rowNumber)}</button></td>
                      <td data-label={tr("Машина")}>
                        <Vehicle number={load.vehicle_number} model={load.vehicle_model} />
                      </td>
                      <td data-label={tr("Водитель")}>
                        <DriverIdentity name={driverName} phone={load.employee_phone} />
                      </td>
                      <LoadWeightCells load={load} />
                      <td data-label={tr("База")} style={{ color: "var(--theme-info-text, #0d6efd)", fontWeight: '600' }}>{tr(baseName)}</td>
                      <td data-label={tr("Въезд КК")}>
                        <div><DateTime value={load.time_a} /></div>
                        {tr(load.geo_a && (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                            <button onClick={event => { event.stopPropagation(); window.open(`https://www.google.com/maps?q=${load.geo_a}`, '_blank'); }} className={styles.geoLinkBtn} aria-label={tr("Открыть геолокацию на карте")}><MapPin size={16} /></button>
                            <button onClick={event => { event.stopPropagation(); toggleMap(load.id, 'geo_a'); }} aria-label={tr("Показать или скрыть мини-карту")} className={styles.mapToggleBtn}>{visibleMaps[mapKeyA] ? <X size={16} /> : <Map size={16} />}</button>
                          </div>
                        ))}
                      </td>
                      <td data-label={tr("Выезд КК")}>
                        <div><DateTime value={load.time_a_exit} /></div>
                        {tr(load.geo_a_exit && (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                            <button onClick={event => { event.stopPropagation(); window.open(`https://www.google.com/maps?q=${load.geo_a_exit}`, '_blank'); }} className={styles.geoLinkBtn} aria-label={tr("Открыть геолокацию на карте")}><MapPin size={16} /></button>
                            <button onClick={event => { event.stopPropagation(); toggleMap(load.id, 'geo_a_exit'); }} aria-label={tr("Показать или скрыть мини-карту")} className={styles.mapToggleBtn}>{visibleMaps[mapKeyAExit] ? <X size={16} /> : <Map size={16} />}</button>
                          </div>
                        ))}
                      </td>
                      <td data-label={tr("Прибытие")}>
                        <div><DateTime value={load.time_b} /></div>
                        {tr(load.geo_b && (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                            <button onClick={event => { event.stopPropagation(); window.open(`https://www.google.com/maps?q=${load.geo_b}`, '_blank'); }} className={styles.geoLinkBtn} aria-label={tr("Открыть геолокацию на карте")}><MapPin size={16} /></button>
                            <button onClick={event => { event.stopPropagation(); toggleMap(load.id, 'geo_b'); }} aria-label={tr("Показать или скрыть мини-карту")} className={styles.mapToggleBtn}>{visibleMaps[mapKeyB] ? <X size={16} /> : <Map size={16} />}</button>
                          </div>
                        ))}
                      </td>
                      <td data-label={tr("Погрузка")}><Duration value={load.duration_str_loading} label={tr("Погрузка")} /></td>
                      <td data-label={tr("Доставка")} style={{ color: "var(--theme-success-text, #198754)" }}><Duration value={load.duration_str} label={tr("Доставка")} /></td>
                      <td data-label={tr("Общее")}><Duration value={load.total_duration_str} label={tr("Общее")} /></td>
                      <td data-label={tr("Примечания")} style={{ fontSize: '14px' }}>
                        {tr(canReadLoadPhotos(user) && load.photo_b && (
                          <div style={{ marginBottom: '6px', textAlign: 'center' }}>
                            <a href={load.photo_b} target="_blank" rel="noopener noreferrer">
                              <img
                                src={load.photo_b}
                                alt={tr("Фото")}
                                style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: "1px solid var(--theme-info-border, #0d6efd)" }}
                              />
                            </a>
                          </div>
                        ))}
                        {tr(load.notes || '-')}
                      </td>
                    </tr>
                    {tr(load.geo_a && visibleMaps[mapKeyA] && (
                      <tr>
                        <td colSpan={14} style={{ padding: 0 }}>
                          <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                            <MiniMap geo={load.geo_a} />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tr(load.geo_a_exit && visibleMaps[mapKeyAExit] && (
                      <tr>
                        <td colSpan={14} style={{ padding: 0 }}>
                          <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                            <MiniMap geo={load.geo_a_exit} />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tr(load.geo_b && visibleMaps[mapKeyB] && (
                      <tr>
                        <td colSpan={14} style={{ padding: 0 }}>
                          <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                            <MiniMap geo={load.geo_b} />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tr(expandedRowId === load.id && (
                      <tr id={`load-details-completed-${load.id}`} className={styles.detailCardRow}>
                        <td colSpan={14}>
                          {renderDetailCard(load)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            ) : !loading && !error ? (
              <tr>
                <td colSpan={14} className={styles.emptyTableState}><EmptyState compact icon={completedSearch?.trim() ? SearchX : CircleCheck} title={completedSearch?.trim() ? tr("По вашему запросу завершённые перевозки не найдены.") : tr("Завершённых перевозок пока нет.")} description={completedSearch?.trim() ? tr("Попробуйте другой запрос или очистите поиск.") : tr("История пополняется после приёма перевозки на базе.")} /></td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={completedPage}
        setPage={setCompletedPage}
        count={completedCount}
        hasNext={!!completedNext}
        hasPrev={!!completedPrev}
        shownCount={completedLoads.length}
        loading={loading || pending}
        style={{ marginTop: '8px', marginBottom: 0 }}
      />
    </div>
  );
};

export default CompletedLoadsTableView;
